/**
 * Nội thất và tìm đường trên lưới (M2) — HÀM THUẦN, không DOM/three.js.
 *
 * - Đặt tự do kệ / tủ lạnh / quầy thu ngân trên lưới trong phạm vi cửa hàng.
 * - Mỗi lần đặt đều kiểm tra còn đường đi từ cửa đến quầy (BFS).
 * - Render (src/render) chỉ gọi các hàm này để vẽ đường đi và hàng đợi.
 *
 * Mọi số (kích thước khu vực, giá nội thất, cấp mở) đọc từ noi-that.json
 * (xuất từ sheet "Nội thất và khu vực"). Hằng thiết kế duy nhất trong code:
 * vị trí cửa (giữa mép nam) và thứ tự ưu tiên ô tương tác — ghi rõ bên dưới.
 */
import type {
  DuLieuGame,
  KhuVuc,
  LoaiNoiThat,
  MonNoiThatDat,
  OLuoi,
  TrangThaiGame,
} from './types.js';

/** Mã loại quầy thu ngân — bắt buộc có ít nhất một cái để khách thanh toán. */
export const MA_QUAY_THU_NGAN = 'QUAY_TN';

/** Kích thước khu vực cửa hàng theo cấp (ô lưới). */
export function kichThuocKhuVuc(capCuaHang: number, duLieu: DuLieuGame): KhuVuc {
  const kv = duLieu.noiThat.khuVuc.find((k) => k.cap === capCuaHang);
  if (!kv) {
    throw new Error(`Không có kích thước khu vực cho cấp cửa hàng ${capCuaHang}.`);
  }
  return kv;
}

/** Danh mục loại nội thất. */
export function danhMucNoiThat(duLieu: DuLieuGame): LoaiNoiThat[] {
  return duLieu.noiThat.noiThat;
}

/** Tìm loại nội thất theo mã. Ném lỗi nếu mã lạ. */
export function timLoaiNoiThat(ma: string, duLieu: DuLieuGame): LoaiNoiThat {
  const loai = duLieu.noiThat.noiThat.find((l) => l.ma === ma);
  if (!loai) {
    throw new Error(`Mã nội thất lạ: ${ma}.`);
  }
  return loai;
}

/**
 * Ô cửa ra vào: giữa mép nam của khu vực (hằng thiết kế M2 — xem decisions-m2.md).
 * Khách đi từ đây vào, và quay lại đây khi ra.
 */
export function oCuaRaVao(kichThuoc: { rong: number; cao: number }): OLuoi {
  return { x: Math.floor(kichThuoc.rong / 2), y: 0 };
}

/** Các ô mà một món nội thất chiếm (gốc trái-dưới). */
export function cacOChiem(mon: { ma: string; x: number; y: number }, loai: LoaiNoiThat): OLuoi[] {
  const o: OLuoi[] = [];
  for (let dy = 0; dy < loai.cao; dy++) {
    for (let dx = 0; dx < loai.rong; dx++) {
      o.push({ x: mon.x + dx, y: mon.y + dy });
    }
  }
  return o;
}

function khoaO(o: OLuoi): string {
  return `${o.x},${o.y}`;
}

/** Tập hợp các ô bị chiếm bởi toàn bộ bố cục. */
export function tapOChiem(boCuc: MonNoiThatDat[], duLieu: DuLieuGame): Set<string> {
  const tap = new Set<string>();
  for (const mon of boCuc) {
    const loai = timLoaiNoiThat(mon.ma, duLieu);
    for (const o of cacOChiem(mon, loai)) {
      tap.add(khoaO(o));
    }
  }
  return tap;
}

function trongKhuVuc(o: OLuoi, kichThuoc: { rong: number; cao: number }): boolean {
  return o.x >= 0 && o.y >= 0 && o.x < kichThuoc.rong && o.y < kichThuoc.cao;
}

/**
 * Kiểm tra có đặt được món mới không: trong khu vực, không chồng lấn,
 * không đè lên ô cửa ra vào.
 */
export function datDuoc(
  mon: { ma: string; x: number; y: number },
  boCuc: MonNoiThatDat[],
  kichThuoc: { rong: number; cao: number },
  duLieu: DuLieuGame,
): boolean {
  const loai = timLoaiNoiThat(mon.ma, duLieu);
  const oMoi = cacOChiem(mon, loai);
  if (oMoi.some((o) => !trongKhuVuc(o, kichThuoc))) return false;
  const cua = oCuaRaVao(kichThuoc);
  const chiem = tapOChiem(boCuc, duLieu);
  return oMoi.every((o) => !chiem.has(khoaO(o)) && khoaO(o) !== khoaO(cua));
}

/**
 * Ô tương tác của một món: ô trống kề món theo thứ tự ưu tiên
 * Nam → Bắc → Tây → Đông (hằng thiết kế M2). Khách đứng đây để
 * lấy hàng / thanh toán. null nếu bị bao kín.
 */
export function oTuongTac(
  mon: { ma: string; x: number; y: number },
  boCuc: MonNoiThatDat[],
  kichThuoc: { rong: number; cao: number },
  duLieu: DuLieuGame,
): OLuoi | null {
  const loai = timLoaiNoiThat(mon.ma, duLieu);
  const chiem = tapOChiem(boCuc, duLieu);
  const ungVien: OLuoi[] = [];
  for (let dx = 0; dx < loai.rong; dx++) {
    ungVien.push({ x: mon.x + dx, y: mon.y - 1 }); // nam
    ungVien.push({ x: mon.x + dx, y: mon.y + loai.cao }); // bắc
  }
  for (let dy = 0; dy < loai.cao; dy++) {
    ungVien.push({ x: mon.x - 1, y: mon.y + dy }); // tây
    ungVien.push({ x: mon.x + loai.rong, y: mon.y + dy }); // đông
  }
  for (const o of ungVien) {
    if (trongKhuVuc(o, kichThuoc) && !chiem.has(khoaO(o))) return o;
  }
  return null;
}

/** Các ô kề 4 hướng còn đi được (trong khu vực, không bị nội thất chiếm). */
function keDiDuoc(o: OLuoi, kichThuoc: { rong: number; cao: number }, chiem: Set<string>): OLuoi[] {
  const ke: OLuoi[] = [];
  for (const d of [
    { x: 1, y: 0 },
    { x: -1, y: 0 },
    { x: 0, y: 1 },
    { x: 0, y: -1 },
  ]) {
    const t = { x: o.x + d.x, y: o.y + d.y };
    if (trongKhuVuc(t, kichThuoc) && !chiem.has(khoaO(t))) ke.push(t);
  }
  return ke;
}

/**
 * Tìm đường ngắn nhất (BFS) từ ô `tu` đến ô `den` trên các ô trống.
 * Trả về mảng ô từ điểm đầu đến điểm cuối (gồm cả hai), null nếu không tới được.
 * Render dùng hàm này để vẽ khách đi lại — logic thuần, test bằng vitest.
 */
export function timDuong(
  tu: OLuoi,
  den: OLuoi,
  boCuc: MonNoiThatDat[],
  kichThuoc: { rong: number; cao: number },
  duLieu: DuLieuGame,
): OLuoi[] | null {
  if (!trongKhuVuc(tu, kichThuoc) || !trongKhuVuc(den, kichThuoc)) return null;
  const chiem = tapOChiem(boCuc, duLieu);
  if (chiem.has(khoaO(tu)) || chiem.has(khoaO(den))) return null;
  const dich = khoaO(den);
  const daTham = new Set<string>([khoaO(tu)]);
  const hangDoi: OLuoi[][] = [[tu]];
  while (hangDoi.length > 0) {
    const duong = hangDoi.shift()!;
    const hienTai = duong[duong.length - 1]!;
    if (khoaO(hienTai) === dich) return duong;
    for (const t of keDiDuoc(hienTai, kichThuoc, chiem)) {
      if (daTham.has(khoaO(t))) continue;
      daTham.add(khoaO(t));
      hangDoi.push([...duong, t]);
    }
  }
  return null;
}

/**
 * Quy tắc đặt nội thất (master-plan 2.4): luôn còn đường đi từ cửa
 * đến quầy thu ngân. Yêu cầu: có ít nhất một quầy, ô tương tác của
 * quầy tới được từ cửa.
 */
export function coDuongDi(
  boCuc: MonNoiThatDat[],
  kichThuoc: { rong: number; cao: number },
  duLieu: DuLieuGame,
): boolean {
  const cua = oCuaRaVao(kichThuoc);
  const quayList = boCuc.filter((m) => m.ma === MA_QUAY_THU_NGAN);
  if (quayList.length === 0) return false;
  for (const quay of quayList) {
    const oTT = oTuongTac(quay, boCuc, kichThuoc, duLieu);
    if (oTT && timDuong(cua, oTT, boCuc, kichThuoc, duLieu)) return true;
  }
  return false;
}

/**
 * Đặt một món nội thất: kiểm tra cấp mở, tiền, vị trí và đường đi.
 * Trừ tiền, thêm vào state.boCuc. Ném lỗi tiếng Việt nếu không đặt được.
 */
export function datNoiThat(
  state: TrangThaiGame,
  duLieu: DuLieuGame,
  ma: string,
  x: number,
  y: number,
): MonNoiThatDat {
  const loai = timLoaiNoiThat(ma, duLieu);
  if (state.capCuaHang < loai.capMo) {
    throw new Error(`${loai.ten} mở từ cấp cửa hàng ${loai.capMo}.`);
  }
  if (state.xu < loai.gia) {
    throw new Error(`Không đủ xu để đặt ${loai.ten} (cần ${loai.gia} xu).`);
  }
  if (!Number.isInteger(x) || !Number.isInteger(y) || x < 0 || y < 0) {
    throw new Error('Vị trí đặt phải là ô lưới (số nguyên ≥ 0).');
  }
  const kichThuoc = kichThuocKhuVuc(state.capCuaHang, duLieu);
  const mon = { ma, x, y };
  if (!datDuoc(mon, state.boCuc, kichThuoc, duLieu)) {
    throw new Error('Không đặt được ở đây: ngoài khu vực, chồng lấn hoặc đè lên cửa.');
  }
  const boCucMoi = [...state.boCuc, { ...mon, uid: state.uidNoiThatTiep }];
  if (!coDuongDi(boCucMoi, kichThuoc, duLieu)) {
    throw new Error('Không đặt được: sẽ chặn đường từ cửa đến quầy thu ngân.');
  }
  state.xu -= loai.gia;
  state.uidNoiThatTiep += 1;
  const monDat: MonNoiThatDat = { ...mon, uid: state.uidNoiThatTiep - 1 };
  state.boCuc.push(monDat);
  return monDat;
}

/**
 * Dỡ một món nội thất. Không hoàn tiền (quyết định M2 — xem decisions-m2.md).
 * Không cho dỡ quầy thu ngân cuối cùng (cửa hàng cần quầy để bán).
 */
export function xoaNoiThat(state: TrangThaiGame, duLieu: DuLieuGame, uid: number): void {
  const viTri = state.boCuc.findIndex((m) => m.uid === uid);
  if (viTri < 0) {
    throw new Error(`Không tìm thấy món nội thất #${uid}.`);
  }
  const mon = state.boCuc[viTri]!;
  if (
    mon.ma === MA_QUAY_THU_NGAN &&
    state.boCuc.filter((m) => m.ma === MA_QUAY_THU_NGAN).length === 1
  ) {
    throw new Error('Không thể dỡ quầy thu ngân cuối cùng.');
  }
  state.boCuc.splice(viTri, 1);
}

/**
 * Bố cục mặc định khi mở game mới: 1 quầy thu ngân + 2 kệ gỗ, miễn phí.
 * Vị trí suy ra từ kích thước khu vực (giữa mép bắc đặt quầy, kệ hai bên thấp).
 */
export function boCucMacDinh(capCuaHang: number, duLieu: DuLieuGame): MonNoiThatDat[] {
  const kv = kichThuocKhuVuc(capCuaHang, duLieu);
  const cx = Math.floor(kv.rong / 2);
  const boCuc: MonNoiThatDat[] = [
    { uid: 0, ma: MA_QUAY_THU_NGAN, x: cx - 1, y: kv.cao - 2 },
    { uid: 1, ma: 'KE', x: 1, y: 1 },
    { uid: 2, ma: 'KE', x: kv.rong - 3, y: 1 },
  ];
  if (!coDuongDi(boCuc, kv, duLieu)) {
    throw new Error(`Bố cục mặc định cấp ${capCuaHang} không có đường đi — kiểm tra số liệu.`);
  }
  return boCuc;
}
